import { StepType } from '@pushly/models/lib/enums/journeys'
import { validateNoConsecutiveSends } from '@pushly/models/lib/structs/journeys/steps/validation'
import {
    JourneyNode,
    JourneyEdge,
    isVariantSplitJourneyNode,
    isConditionalStepJourneyNode,
} from '../../types/journey-nodes'
import type { IBranch, IVariantBranch } from '@pushly/models/lib/structs/journeys'
import { getVariantPathOwnership } from './traversal'
import { getNodeOutStepId } from './traversal'
import { isBranchingNode, getBranchingNodeData } from './traversal'
import { ScopeCache } from './scope'
import { validateGraphInvariants } from './invariants'

// ---- Error types ----

export type BranchFieldError = {
    field: 'name' | 'percentage'
    branchKey: string
    message: string
}

/** @deprecated Use BranchFieldError instead */
export type FieldError = BranchFieldError

export type SummaryError = {
    field: 'percentageSum'
    message: string
}

export type BranchValidationError = BranchFieldError | SummaryError

export type FieldValidationResult = {
    errors: BranchValidationError[]
    valid: boolean
}

export type ConditionFieldError = {
    field: 'condition.field' | 'condition.operator'
    message: string
}

export type ConditionFieldValidationResult = {
    errors: ConditionFieldError[]
    valid: boolean
}

export type GraphError = {
    message: string
}

export type GraphValidationResult = {
    errors: GraphError[]
    valid: boolean
}

// ---- Field-level validation ----

/**
 * Validates branch labels for any branch type (operates on IBranch[] union).
 * Checks for empty/whitespace-only labels, labels exceeding 50 characters,
 * and duplicate trimmed labels.
 */
export function validateBranchLabels(branches: IBranch[]): BranchFieldError[] {
    const errors: BranchFieldError[] = []

    const seenLabels = new Map<string, number>()
    for (const branch of branches) {
        const trimmed = branch.label.trim()

        if (trimmed.length === 0) {
            errors.push({ field: 'name', branchKey: branch.key, message: 'Branch name is required' })
        } else if (trimmed.length > 50) {
            errors.push({ field: 'name', branchKey: branch.key, message: 'Name must be 50 characters or fewer' })
        }

        if (trimmed.length > 0) {
            seenLabels.set(trimmed, (seenLabels.get(trimmed) ?? 0) + 1)
        }
    }

    for (const branch of branches) {
        const trimmed = branch.label.trim()
        if (trimmed.length > 0 && (seenLabels.get(trimmed) ?? 0) > 1) {
            if (!errors.some((e) => e.branchKey === branch.key)) {
                errors.push({ field: 'name', branchKey: branch.key, message: 'Branch names must be unique' })
            }
        }
    }

    return errors
}

/**
 * Validates variant-specific percentage constraints (operates on IVariantBranch[]).
 * Checks for non-integer percentages, out-of-range values, and sum !== 100.
 */
export function validateVariantPercentages(branches: IVariantBranch[]): BranchValidationError[] {
    const errors: BranchValidationError[] = []
    let sum = 0

    for (const branch of branches) {
        if (!Number.isInteger(branch.percentage)) {
            errors.push({ field: 'percentage', branchKey: branch.key, message: 'Percentage must be a whole number' })
        } else if (branch.percentage < 1 || branch.percentage > 99) {
            errors.push({ field: 'percentage', branchKey: branch.key, message: 'Percentage must be between 1 and 99' })
        }
        sum += branch.percentage
    }

    if (sum !== 100) {
        errors.push({ field: 'percentageSum', message: `Percentages must sum to 100 (currently ${sum})` })
    }

    return errors
}

/**
 * Top-level branch field validator. Dispatches to shared label validation
 * and type-specific percentage validation based on step type.
 */
export function validateBranchFields(branches: IBranch[], stepType: StepType): FieldValidationResult {
    const errors: BranchValidationError[] = []

    // Shared: label validation (operates on IBranch[])
    errors.push(...validateBranchLabels(branches))

    // Type-specific: percentage validation (operates on IVariantBranch[])
    if (stepType === StepType.VARIANT_SPLIT) {
        const variantBranches = branches as IVariantBranch[]
        errors.push(...validateVariantPercentages(variantBranches))
    }

    return { errors, valid: errors.length === 0 }
}

/**
 * @deprecated Use validateBranchFields() instead.
 * Kept for backward compatibility during migration.
 */
export function validateSplitStepFields(branches: IVariantBranch[]): FieldValidationResult {
    return validateBranchFields(branches, StepType.VARIANT_SPLIT)
}

/**
 * Validates the condition step configuration fields.
 * Used by the condition editor on every input change.
 */
export function validateConditionStepFields(
    condition: { field: string; operator: string },
): ConditionFieldValidationResult {
    const errors: ConditionFieldError[] = []

    if (!condition.field || condition.field.trim().length === 0) {
        errors.push({ field: 'condition.field', message: 'Condition field is required' })
    }

    if (!condition.operator || condition.operator.trim().length === 0) {
        errors.push({ field: 'condition.operator', message: 'Condition operator is required' })
    }

    return { errors, valid: errors.length === 0 }
}

// ---- Graph-level validation ----

/**
 * Validates the entire journey graph for branching step constraints.
 * Runs generic branching invariant checks for all branching nodes,
 * plus type-specific field validation for splits and conditions.
 * Used on save before persisting.
 */
export function validateSplitStepGraph(nodes: JourneyNode[], edges: JourneyEdge[] = []): GraphValidationResult {
    const errors: GraphError[] = []
    const nodeMap = new Map<string, JourneyNode>()
    for (const node of nodes) {
        nodeMap.set(node.id, node)
    }

    // --- Generic branching invariant validation (all branching nodes) ---
    const hasBranchingNodes = nodes.some(isBranchingNode)
    if (hasBranchingNodes) {
        const scopeCache = new ScopeCache()
        const invariantErrors = validateGraphInvariants(nodes, edges, scopeCache)
        for (const err of invariantErrors) {
            errors.push({ message: err.message })
        }
    }

    // --- Condition-specific field validation ---
    const conditionNodes = nodes.filter(isConditionalStepJourneyNode)
    for (const condNode of conditionNodes) {
        const config = condNode.data.step.configuration
        const fieldResult = validateConditionStepFields(config.condition)
        if (!fieldResult.valid) {
            for (const err of fieldResult.errors) {
                errors.push({ message: err.message })
            }
        }
    }

    // --- Variant-split-specific field validation ---
    const splitNodes = nodes.filter(isVariantSplitJourneyNode)

    // Validate each split step independently
    for (const splitNode of splitNodes) {
        const config = splitNode.data.step.configuration

        // Split configuration is valid (delegate to field-level)
        const fieldResult = validateBranchFields(config.branches, StepType.VARIANT_SPLIT)
        if (!fieldResult.valid) {
            for (const err of fieldResult.errors) {
                errors.push({ message: err.message })
            }
        }

        // join_step_id is defined
        if (config.join_step_id == null) {
            errors.push({ message: 'All variant paths must converge at a single step' })
        }

        // All branch out_step_ids are non-null
        for (const branch of config.branches) {
            if (branch.out_step_id == null) {
                errors.push({ message: `Branch '${branch.label}' has no connected step` })
            }
        }

        // Every branch path reaches join target (no cycles)
        if (config.join_step_id != null) {
            const joinKey = String(config.join_step_id)

            for (const branch of config.branches) {
                if (branch.out_step_id == null) continue

                const reached = walkPathToTarget(String(branch.out_step_id), joinKey, nodeMap)
                if (!reached) {
                    errors.push({ message: `Branch '${branch.label}' does not reach the convergence point` })
                }
            }

            // Join target reaches EXIT
            if (nodeMap.has(joinKey)) {
                const reachesExit = walkPathToExit(joinKey, nodeMap)
                if (!reachesExit) {
                    errors.push({ message: 'The convergence step must eventually lead to the exit' })
                }
            }
        }

        // No cross-branch connections before join target
        if (config.join_step_id != null) {
            const ownership = getVariantPathOwnership(splitNode, nodes)
            if (hasCrossBranchConnections(ownership, nodeMap)) {
                errors.push({ message: 'Steps in different variant paths cannot be connected' })
            }
        }
    }

    // No cycles (valid DAG)
    if (hasCycle(nodes, nodeMap)) {
        errors.push({ message: 'Journey contains a cycle — steps must not loop back' })
    }

    // No orphaned steps
    if (hasOrphanedSteps(nodes, nodeMap)) {
        errors.push({ message: 'Journey contains unreachable steps' })
    }

    // --- Consecutive send validation (graph-aware) ---
    const stepsForValidation = nodes
        .filter((n) => !n.hidden)
        .map((n) => n.data.step)
    const sendViolations = validateNoConsecutiveSends(stepsForValidation)
    for (const v of sendViolations) {
        errors.push({
            message: 'Send steps cannot appear consecutively without a delay between them.',
        })
    }

    return { errors, valid: errors.length === 0 }
}

// ---- Internal helpers ----

/**
 * Walks from startId following out_step_id until reaching targetId.
 * For branching nodes, jumps to join_step_id.
 * Returns true if targetId is reached, false on dead end or cycle.
 */
function walkPathToTarget(startId: string, targetId: string, nodeMap: Map<string, JourneyNode>): boolean {
    const visited = new Set<string>()
    let currentId: string | null = startId

    while (currentId != null) {
        if (currentId === targetId) return true
        if (visited.has(currentId)) return false
        visited.add(currentId)

        const node = nodeMap.get(currentId)
        if (!node) return false

        currentId = getNodeOutStepId(node)
    }

    return false
}

/**
 * Walks from startId following out_step_id until reaching an EXIT step.
 * For branching nodes, jumps to join_step_id.
 * Returns true if EXIT is reached.
 */
function walkPathToExit(startId: string, nodeMap: Map<string, JourneyNode>): boolean {
    const visited = new Set<string>()
    let currentId: string | null = startId

    while (currentId != null) {
        if (visited.has(currentId)) return false
        visited.add(currentId)

        const node = nodeMap.get(currentId)
        if (!node) return false

        if (node.data.step.type === StepType.EXIT) return true

        currentId = getNodeOutStepId(node)
    }

    return false
}

/**
 * Checks if the journey graph contains any cycles.
 * Traverses from TRIGGER (or GLOBAL_TRIGGER) following all out_step_id links,
 * including branching through split step variants.
 */
function hasCycle(
    nodes: JourneyNode[],
    nodeMap: Map<string, JourneyNode>,
): boolean {
    // DFS-based cycle detection
    const WHITE = 0 // unvisited
    const GRAY = 1 // in current path
    const BLACK = 2 // fully processed

    const color = new Map<string, number>()
    for (const node of nodes) {
        color.set(node.id, WHITE)
    }

    function dfs(nodeId: string): boolean {
        color.set(nodeId, GRAY)

        const node = nodeMap.get(nodeId)
        if (!node) {
            color.set(nodeId, BLACK)
            return false
        }

        // Get successors
        const successors = getSuccessors(node)

        for (const succId of successors) {
            const succColor = color.get(succId)
            if (succColor === GRAY) return true // back edge = cycle
            if (succColor === WHITE) {
                if (dfs(succId)) return true
            }
        }

        color.set(nodeId, BLACK)
        return false
    }

    // Start from all entry points (TRIGGER or GLOBAL_TRIGGER)
    for (const node of nodes) {
        if (
            node.data.step.type === StepType.TRIGGER ||
            node.data.step.type === StepType.GLOBAL_TRIGGER
        ) {
            if (color.get(node.id) === WHITE) {
                if (dfs(node.id)) return true
            }
        }
    }

    return false
}

/**
 * Checks if there are steps not reachable from the entry points.
 */
function hasOrphanedSteps(
    nodes: JourneyNode[],
    nodeMap: Map<string, JourneyNode>,
): boolean {
    const reachable = new Set<string>()

    function visit(nodeId: string) {
        if (reachable.has(nodeId)) return
        reachable.add(nodeId)

        const node = nodeMap.get(nodeId)
        if (!node) return

        const successors = getSuccessors(node)
        for (const succId of successors) {
            visit(succId)
        }
    }

    // Start from entry points
    for (const node of nodes) {
        if (
            node.data.step.type === StepType.TRIGGER ||
            node.data.step.type === StepType.GLOBAL_TRIGGER
        ) {
            visit(node.id)
        }
    }

    // Check if all non-hidden nodes are reachable
    for (const node of nodes) {
        if (!node.hidden && !reachable.has(node.id)) {
            return true
        }
    }

    return false
}

/**
 * Checks if any step in one variant path connects to a step in a different variant path.
 */
function hasCrossBranchConnections(
    ownership: Map<string, string>,
    nodeMap: Map<string, JourneyNode>,
): boolean {
    for (const [stepId, ownerVariantId] of ownership) {
        if (ownerVariantId === '__shared__') continue

        const node = nodeMap.get(stepId)
        if (!node) continue

        const outStepId = getNodeOutStepId(node)
        if (outStepId == null) continue

        const targetOwner = ownership.get(outStepId)
        if (targetOwner != null && targetOwner !== '__shared__' && targetOwner !== ownerVariantId) {
            return true
        }
    }

    return false
}

/**
 * Gets all successor node IDs for a given node.
 * For branching nodes, returns all branch out_step_ids.
 * For other steps, returns the single out_step_id.
 */
function getSuccessors(
    node: JourneyNode,
): string[] {
    if (isBranchingNode(node)) {
        const data = getBranchingNodeData(node)
        if (data) {
            return data.branches
                .map((b) => b.out_step_id)
                .filter((id): id is string | number => id != null)
                .map(String)
        }
    }

    const outStepId = getNodeOutStepId(node)
    return outStepId != null ? [outStepId] : []
}


